import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { Badge } from '@shared/components/ui/badge';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { formatDate } from '@shared/utils/FormatUtils';
import { COURIER_INVOICES_BASE_PATH, COURIER_SETTLEMENTS_BASE_PATH } from '../../../constants';
import { useCourierInvoiceDetail } from '../../hooks/useCourierInvoiceDetail';
import { ErrorState } from '../../../List/components/feedback/ErrorState';
import { LoadingState } from '../../../List/components/feedback/LoadingState';
import { ImportCourierInvoiceModal } from '../interaction';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface InvoiceDetailMainProps {
  invoiceId: number;
}

const statusBadgeVariant = (status: string | null | undefined) => {
  const s = (status || '').toLowerCase();
  if (s === 'draft') return 'transparent-orange' as const;
  if (s === 'imported') return 'transparent-green' as const;
  return 'outline' as const;
};

export const InvoiceDetailMain = ({ invoiceId }: InvoiceDetailMainProps) => {
  const { t } = useTranslation('courierInvoicesModule');
  const [importOpen, setImportOpen] = useState(false);

  const { data, isLoading, isError, error, refetch, isFetching } = useCourierInvoiceDetail(invoiceId);

  const breadcrumbs = useMemo(
    () => [
      { label: t('breadcrumbs.courier_invoices'), path: COURIER_INVOICES_BASE_PATH },
      { label: t('detail.title', { id: invoiceId }) },
    ],
    [t, invoiceId],
  );

  const headerActions = useMemo(
    () => [
      {
        id: 'courier-invoice-import',
        label: t('detail.import_button'),
        onClick: () => setImportOpen(true),
        variant: 'primary' as const,
      },
    ],
    [t],
  );

  usePageNavigation({
    breadcrumbs,
    headerActions,
  });

  if (isLoading && !data) {
    return (
      <div className="px-6 pt-4">
        <LoadingState message={t('loading.detail')} className="min-h-[280px]" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="px-6 pt-4">
        <ErrorState error={error ?? new Error(t('errors.unexpected'))} onRetry={() => void refetch()} />
      </div>
    );
  }

  const { invoice, aggregates, recent_settlements } = data;

  const renderStatusLabel = (status: string | null | undefined) => {
    const key = (status || '').toLowerCase();
    if (key === 'draft') return t('status.draft');
    if (key === 'imported') return t('status.imported');
    return t('status.unknown', { status: status ?? '—' });
  };

  const fmtMoney = (n: number) =>
    new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

  const summaryCards = [
    {
      title: t('list.columns.courier'),
      value: invoice.courier?.name ?? '—',
    },
    {
      title: t('list.columns.status'),
      value: <Badge variant={statusBadgeVariant(invoice.status)}>{renderStatusLabel(invoice.status)}</Badge>,
    },
    {
      title: t('detail.aggregates.settlements'),
      value: aggregates.settlements_count,
    },
    {
      title: t('detail.aggregates.charge_lines_total'),
      value: fmtMoney(aggregates.courier_charge_lines_total),
    },
  ];

  return (
    <div className="px-6 pt-4 pb-8">
      <div className={`mb-6 transition-opacity duration-200 ${isFetching ? 'opacity-70' : 'opacity-100'}`}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {summaryCards.map((card, index) => (
            <SectionBox key={index}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm text-subtext mb-1">{card.title}</h3>
                  <div className="text-md text-title break-words">{card.value}</div>
                </div>
              </div>
            </SectionBox>
          ))}
        </div>
      </div>

      <SectionBox title={t('detail.summary_section')} className="mb-6">
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div>
            <dt className="text-subtext">{t('list.columns.supplier_reference')}</dt>
            <dd className="text-title">{invoice.supplier_reference ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-subtext">{t('list.columns.invoiced_at')}</dt>
            <dd className="text-title">{invoice.invoiced_at ? formatDate(invoice.invoiced_at) : '—'}</dd>
          </div>
          <div>
            <dt className="text-subtext">{t('detail.period')}</dt>
            <dd className="text-title">
              {invoice.period_start || invoice.period_end
                ? `${invoice.period_start ? formatDate(invoice.period_start) : '—'} → ${invoice.period_end ? formatDate(invoice.period_end) : '—'}`
                : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-subtext">{t('detail.currency')}</dt>
            <dd className="text-title">{invoice.currency ?? '—'}</dd>
          </div>
          {invoice.notes ? (
            <div className="sm:col-span-2">
              <dt className="text-subtext">{t('detail.notes')}</dt>
              <dd className="text-title whitespace-pre-wrap">{invoice.notes}</dd>
            </div>
          ) : null}
        </dl>
      </SectionBox>

      <SectionBox title={t('detail.recent_imports')}>
        <div className="rounded-lg border border-card-border overflow-hidden shadow-sm">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-card-border bg-muted/30">
                <TableHead className="text-foreground font-medium">{t('detail.columns.file')}</TableHead>
                <TableHead className="text-foreground font-medium">{t('detail.columns.status')}</TableHead>
                <TableHead className="text-right text-foreground font-medium">{t('detail.columns.lines')}</TableHead>
                <TableHead className="text-right text-foreground font-medium">{t('detail.columns.matched')}</TableHead>
                <TableHead className="text-foreground font-medium">{t('detail.columns.imported_at')}</TableHead>
                <TableHead className="text-right text-foreground font-medium" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {recent_settlements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    {t('settlement.empty_rows')}
                  </TableCell>
                </TableRow>
              ) : (
                recent_settlements.map((s) => (
                  <TableRow key={s.id} className="border-card-border hover:bg-muted/30 transition-colors">
                    <TableCell className="text-foreground max-w-[220px] truncate" title={s.source_filename ?? ''}>
                      {s.source_filename ?? '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">{s.import_status ?? '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{s.lines_total ?? '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{s.lines_matched_count ?? '—'}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {s.imported_at ? formatDate(s.imported_at) : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Link
                        to={`${COURIER_SETTLEMENTS_BASE_PATH}/${s.id}`}
                        className="text-primary text-sm font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                      >
                        {t('detail.open_settlement')}
                      </Link>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </SectionBox>

      <ImportCourierInvoiceModal invoiceId={invoiceId} isOpen={importOpen} onClose={() => setImportOpen(false)} />
    </div>
  );
};
